import { c as _c } from "react/compiler-runtime";
import type { Nexperience } from '@data-access/graphql/schema.types';
import type { NexperienceFragment } from '@data-access/lib/gql/experience/documents';
import { dateParser } from '@feat/feat-url-state/date';
import { serializeUrlState } from '@feat/feat-url-state/schema';
import { useUrlState } from '@feat/feat-url-state/use-url-state';
import { objectKeys, shortenText } from '@utils/helpers';
import { baseUrl } from '@utils/utils-config';
import { formatDuration } from '@utils/utils-format';
import { pick } from 'es-toolkit';
import { useMemo, useState } from 'react';

// FR7-1196 update type once backend is ready
export type UseExperienceData = Pick<NonNullable<NexperienceFragment>, 'duration' | 'publicId' | 'slug' | 'title' | 'metaTitle' | 'metaDescription' | 'location' | 'destination' | 'basePrice' | 'priceCategory' | 'hasMultipleDurations'>;
export function useExperienceData(experience) {
  const $ = _c(9);
  const locationURL = useExperienceDestinationURL(experience.destination?.slug);
  const questionnaireURL = `/service/anfrageformular-erlebnisse/?experienceId=${experience.publicId}`;
  const duration = useExperienceDuration(experience.duration, experience.hasMultipleDurations);
  const metaData = useExperienceMetaData(experience);
  let t0;
  if ($[0] !== experience.basePrice || $[1] !== experience.priceCategory) {
    t0 = !experience.basePrice || !experience.priceCategory ? null : {
      price: experience.basePrice,
      category: experience.priceCategory
    };
    $[0] = experience.basePrice;
    $[1] = experience.priceCategory;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== duration || $[4] !== locationURL || $[5] !== metaData || $[6] !== questionnaireURL || $[7] !== t0) {
    t1 = {
      locationURL,
      questionnaireURL,
      duration,
      price: t0,
      ...metaData
    };
    $[3] = duration;
    $[4] = locationURL;
    $[5] = metaData;
    $[6] = questionnaireURL;
    $[7] = t0;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  return t1;
}
export const useExperienceURL = t0 => {
  const {
    publicId,
    slug
  } = t0;
  const {
    urlState
  } = useUrlState();
  const searchParams = serializeUrlState(pick(urlState, objectKeys(dateParser)));
  return `${getExperienceUrl({
    publicId,
    slug
  })}${searchParams}`;
};
export function useExperienceDuration(duration, hasMultipleDurations) {
  const $ = _c(6);
  let t0;
  bb0: {
    if (!duration) {
      t0 = "";
      break bb0;
    }
    if (duration % 60 === 0 || duration >= 1440) {
      const hours = Math.floor(duration / 60);
      const days = Math.floor(hours / 24);
      const t1 = hours % 24;
      let t2;
      if ($[0] !== days || $[1] !== t1) {
        t2 = formatDuration({
          days,
          hours: t1
        }, {
          delimiter: " und ",
          zero: false
        });
        $[0] = days;
        $[1] = t1;
        $[2] = t2;
      } else {
        t2 = $[2];
      }
      t0 = t2;
      break bb0;
    }
    const hours_0 = Math.floor(duration / 60);
    const minutes = duration % 60;
    let t1;
    if ($[3] !== hours_0 || $[4] !== minutes) {
      t1 = formatDuration({
        hours: hours_0,
        minutes
      }, {
        delimiter: " und ",
        zero: false
      });
      $[3] = hours_0;
      $[4] = minutes;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    t0 = t1;
  }
  const formattedDuration = t0;
  if (!duration) {
    return "";
  }
  if (!hasMultipleDurations) {
    return formattedDuration;
  }
  return `ab ${formattedDuration}`;
}
const useExperienceMetaData = (experience: Pick<Nexperience, 'metaTitle' | 'metaDescription' | 'title'>) => {
  const metaTitle = shortenText(experience.metaTitle || experience.title, 70);
  const metaDescription = shortenText(experience.metaDescription, 175, true, true);
  return {
    metaTitle,
    metaDescription
  };
};
export const useExperienceDestinationURL = slug => {
  const $ = _c(5);
  const {
    urlState
  } = useUrlState();
  let t0;
  if ($[0] !== urlState) {
    t0 = serializeUrlState(pick(urlState, objectKeys(dateParser)));
    $[0] = urlState;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const searchParams = t0;
  let t1;
  if ($[2] !== searchParams || $[3] !== slug) {
    t1 = () => {
      if (!slug) {
        return "";
      }
      return `/reiseziele/${slug}/${searchParams}`;
    };
    $[2] = searchParams;
    $[3] = slug;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const [destinationUrl] = useState(t1);
  return destinationUrl;
};
export const getExperienceUrl = ({
  publicId,
  slug
}: {
  publicId: number;
  slug: string;
}) => baseUrl(`/erlebnisse/${publicId}/${slug}/`);